/**
 * Host a casual match (non-tournament). Any player can create a match with venue, court, date/time.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { collection, getDocs, addDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { parseLocalToUTC, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import VenueSearch from '../components/VenueSearch';
import { useAlert } from '../contexts/AlertContext';
import './HostMatch.css';

export default function HostMatch() {
  const { currentUser, userProfile } = useAuth();
  const { showToast } = useAlert();
  const { gameId } = useGame();
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    venueId: '',
    courtId: '',
    date: '',
    time: '',
    matchType: 'singles',
    opponentEmail: ''
  });

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'venues'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setVenues(list.filter(v => (v.gameIds || []).includes(gameId || 'table-tennis')));
      } catch (err) {
        console.error('Error fetching venues:', err);
        setError('Failed to load venues');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser, gameId]);

  async function resolveOpponentEmail(email) {
    if (!email || !email.trim()) return null;
    const trimmed = email.trim().toLowerCase();
    const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', trimmed)));
    if (usersSnap.empty) return null;
    return usersSnap.docs[0].id;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!currentUser?.uid) return;
    if (!form.venueId || !form.date || !form.time) {
      setError('Please select venue, date and time.');
      return;
    }
    const venueInFirestore = venues.some(v => v.id === form.venueId);
    if (!venueInFirestore) {
      setError('This venue must be claimed first. Go to Venues, search for it, and click "Claim this venue" before hosting a match here.');
      return;
    }
    setSaving(true);
    try {
      const opponentId = await resolveOpponentEmail(form.opponentEmail);
      const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
      const scheduledAtMs = parseLocalToUTC(form.date, form.time, userTimezone);
      const scheduledAt = Timestamp.fromMillis(scheduledAtMs);
      const d = new Date(scheduledAtMs);
      const scheduledTimeUtc = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
      const payload = {
        tournamentId: null,
        gameId: gameId || 'table-tennis',
        venueId: form.venueId || null,
        courtId: form.courtId || null,
        scheduledAt,
        scheduledDate: scheduledAt,
        scheduledTime: scheduledTimeUtc,
        matchType: form.matchType || 'singles',
        player1Id: currentUser.uid,
        player2Id: opponentId || null,
        status: 'scheduled',
        phase: 'group',
        createdBy: currentUser.uid,
        createdAt: new Date()
      };
      const ref = await addDoc(collection(db, 'matches'), payload);
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.MATCH, ref.id, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName
      }, { casual: true, matchType: payload.matchType }, { venueId: payload.venueId });
      showToast('Match created! You can view it on the Schedule.', { variant: 'success' });
      navigate('/schedule');
    } catch (err) {
      console.error('Error creating match:', err);
      setError(err.message || 'Failed to create match');
    } finally {
      setSaving(false);
    }
  }

  const venueForCourts = venues.find(v => v.id === form.venueId) || selectedVenue;
  const courts = venueForCourts?.courts || [];

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="host-match-page">
      <h1>Host a casual match</h1>
      <p className="host-match-subtitle">Create a match outside of a tournament. Search or select venue, court, date and time.</p>
      <form onSubmit={handleSubmit} className="host-match-form">
        {error && <div className="host-match-error">{error}</div>}
        <div className="form-group">
          <label>Venue *</label>
          <VenueSearch
            gameId={gameId}
            firestoreVenues={venues}
            placeholder="Search by pincode, area, city or venue name..."
            onSelect={(venue) => {
              setSelectedVenue(venue || null);
              setForm((prev) => ({ ...prev, venueId: venue?.id || '', courtId: '' }));
            }}
          />
          {form.venueId && venueForCourts && (
            <>
              <p className="host-match-venue-selected">
                Selected: <strong>{venueForCourts.name}</strong>
                {venueForCourts.address && ` · ${venueForCourts.address}`}
                {(venueForCourts.courtCount ?? (venueForCourts.courts || []).length) > 0 && (
                  <> · {(venueForCourts.courtCount ?? (venueForCourts.courts || []).length)} court(s)</>
                )}
                {venueForCourts.availabilityText && (
                  <span className="host-match-venue-availability"> · {venueForCourts.availabilityText}</span>
                )}
              </p>
              {!venues.some(v => v.id === form.venueId) && (
                <p className="host-match-venue-claim-hint">Claim this venue on the Venues page first to host a match here.</p>
              )}
            </>
          )}
        </div>
        <div className="form-group">
          <label>Court (optional)</label>
          <select value={form.courtId} onChange={(e) => setForm({ ...form, courtId: e.target.value || '' })} disabled={!form.venueId}>
            <option value="">— Any court —</option>
            {courts.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Date *</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Time *</label>
            <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required />
          </div>
        </div>
        <div className="form-group">
          <label>Match type</label>
          <select value={form.matchType} onChange={(e) => setForm({ ...form, matchType: e.target.value })}>
            <option value="singles">Singles</option>
            <option value="doubles">Doubles (add opponents later)</option>
          </select>
        </div>
        {form.matchType === 'singles' && (
          <div className="form-group">
            <label>Opponent email (optional)</label>
            <input type="email" placeholder="Leave empty to fill later" value={form.opponentEmail} onChange={(e) => setForm({ ...form, opponentEmail: e.target.value })} />
          </div>
        )}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create match'}</button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/schedule')}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
